import { useCallback, useState } from "react";
import {
  CreditNoteDocumentType,
  CreditNoteEntity,
  CreditNoteListStatus,
  GetAllCreditNotesUseCase,
  PaginationInfo,
} from "../../domain";
import { CreditNoteRepositoryImpl } from "../../data/repositories/CreditNoteRepositoryImpl";

const creditNoteRepository = new CreditNoteRepositoryImpl();
const getAllCreditNotesUseCase = new GetAllCreditNotesUseCase(
  creditNoteRepository
);

export interface CreditNoteListParams {
  page?: number;
  limit?: number;
  status?: CreditNoteListStatus;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: "created_at" | "updated_at" | "document_number" | "status";
  sortOrder?: "asc" | "desc";
  documentType?: CreditNoteDocumentType;
}

export const useCreditNoteList = () => {
  const [creditNotes, setCreditNotes] = useState<CreditNoteEntity[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCreditNotes = useCallback(
    async (params: CreditNoteListParams = {}) => {
      setLoading(true);
      setError(null);

      const limit = params.limit ?? 10;
      const page = params.page ?? 1;

      try {
        const result = await getAllCreditNotesUseCase.execute({
          limit,
          offset: Math.max(page - 1, 0) * limit,
          status: params.status ?? "all",
          search: params.search?.trim() || undefined,
          date_from: params.dateFrom,
          date_to: params.dateTo,
          sort_by: params.sortBy ?? "created_at",
          sort_order: params.sortOrder ?? "desc",
          document_type: params.documentType ?? "all",
        });

        setCreditNotes(result.creditNotes);
        setPagination(result.pagination);
      } catch (error: any) {
        setError(error.message || "Failed to load credit notes");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const clearError = useCallback(() => setError(null), []);

  return {
    creditNotes,
    pagination,
    loading,
    error,
    fetchCreditNotes,
    clearError,
  };
};
